import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClientCorporateAddressUpdateMutation, useCountryByIdQuery } from 'api';
import { Form, FormProps, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { ClientCorporateAddressFormType } from 'typings/form';
import { IAddressUpdate } from './index';

export const useAddressUpdate = (props: IAddressUpdate) => {
  const { modalState, dataToEdit, onModalStateChange } = props;
  const [states, setStates] = useState([]);
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { data: countryData } = useCountryByIdQuery(
    { countryId: dataToEdit.country },
    {
      skip: !modalState || !dataToEdit,
    }
  );
  const [clientCorporateAddressUpdate, { isLoading }] = useClientCorporateAddressUpdateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );
  const initValues = useMemo(() => {
    return {
      country: dataToEdit.country,
      state: dataToEdit.state,
      city: dataToEdit.city,
      streetAddress: dataToEdit.street,
      zip: dataToEdit.zip,
    };
  }, [dataToEdit]);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setStates([]);
  }, [form, onModalStateChange]);

  const onFinish: FormProps<ClientCorporateAddressFormType>['onFinish'] = useCallback(
    (values: ClientCorporateAddressFormType) => {
      const payload = values;
      payload.clientId = id as string;
      clientCorporateAddressUpdate(payload)
        .then(({ data, error }: any) => {
          if (data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: 'The client address successfully updated.',
            });
            onCancel();
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [clientCorporateAddressUpdate, openNotificationWithIcon, id, onCancel]
  );

  const handleCountryChange = useCallback(
    (_: string, value: Record<string, any>) => {
      setStates(value.states || []);
      form.setFieldsValue({ state: '' });
    },
    [form]
  );

  useEffect(() => {
    if (modalState && initValues) {
      form.setFieldsValue(initValues);
    }
    if (countryData?.states.length) {
      setStates(countryData.states);
    }
  }, [form, initValues, modalState, countryData]);

  return {
    form,
    contextHolder,
    isLoading,
    states,
    onFinish,
    onCancel,
    handleCountryChange,
  };
};
